import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, Gift, TrendingUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const fmtR = (v) =>
  `R ${(parseFloat(v) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

const today = () => new Date().toISOString().split("T")[0];
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split("T")[0];
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-ZA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function VoucherAuditReport() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(firstOfMonth());
  const [endDate, setEndDate] = useState(today());
  const [promoOptions, setPromoOptions] = useState([]);
  const [promotionId, setPromotionId] = useState("ALL");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const loadPromos = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/promotions/spend-reward-list`);
      setPromoOptions(r.data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const runReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (promotionId && promotionId !== "ALL") params.promotion_id = promotionId;
      const r = await axios.get(`${API}/promotions/voucher-audit`, { params });
      setReport(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, promotionId]);

  useEffect(() => {
    loadPromos();
    runReport();
  }, []);

  const exportCsv = () => {
    if (!report?.transactions?.length) {
      toast.info("Nothing to export");
      return;
    }
    const rows = [
      [
        "Issued Date/Time",
        "Code",
        "Status",
        "Promotion",
        "Sale #",
        "Cashier",
        "Customer",
        "Expires",
        "Redeemed At",
        "Redeemed As",
        "Redeemed Sale #",
        "Voucher Value (R)",
      ],
    ];
    for (const t of report.transactions) {
      rows.push([
        fmtDateTime(t.created_at),
        t.code || "",
        t.status || "",
        t.promotion_name || t.promotion_id || "",
        t.transaction_number || "",
        t.cashier_name || "",
        t.customer_name || "",
        t.expiry_date
          ? new Date(t.expiry_date).toLocaleDateString("en-ZA")
          : "",
        t.redeemed_at ? new Date(t.redeemed_at).toLocaleString("en-ZA") : "",
        t.redeemed_as || "",
        t.redeemed_sale_number || "",
        (t.voucher_value || 0).toFixed(2),
      ]);
    }
    const csv = rows
      .map((r) =>
        r
          .map((c) => {
            const s = String(c ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `voucher-audit-${startDate}_to_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto" data-testid="voucher-audit-page">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/reports")}
          data-testid="voucher-audit-back"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Reports
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Gift className="h-6 w-6 text-purple-600" /> Voucher Audit Report
        </h1>
      </div>
      <p className="text-sm text-gray-600 -mt-2">
        Every sale that issued a spend-reward voucher, so you can verify no
        more vouchers were printed than qualifying transactions. Refunds are
        excluded.
      </p>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="va-start-date"
              />
            </div>
            <div>
              <Label className="text-xs">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="va-end-date"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Promotion</Label>
              <Select value={promotionId} onValueChange={setPromotionId}>
                <SelectTrigger data-testid="va-promo-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All spend-reward promotions</SelectItem>
                  {promoOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} {p.is_active ? "" : "(inactive)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={runReport}
                disabled={loading}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                data-testid="va-run"
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  "Run"
                )}
              </Button>
              <Button
                variant="outline"
                onClick={exportCsv}
                disabled={!report?.transactions?.length}
                data-testid="va-export-csv"
              >
                <Download className="h-4 w-4 mr-1" /> CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="border-purple-200">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Gift className="h-4 w-4" /> Issued
              </CardDescription>
              <CardTitle className="text-2xl" data-testid="va-summary-issued">
                {report.summary.vouchers_issued}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/40">
            <CardHeader className="pb-2">
              <CardDescription>Active (Outstanding)</CardDescription>
              <CardTitle className="text-2xl text-emerald-700" data-testid="va-summary-active">
                {report.summary.counts_by_status?.ACTIVE || 0}
              </CardTitle>
              <CardDescription className="text-xs">
                {fmtR(report.summary.total_outstanding_value)}
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="border-gray-200 bg-gray-50/40">
            <CardHeader className="pb-2">
              <CardDescription>Redeemed</CardDescription>
              <CardTitle className="text-2xl text-gray-700" data-testid="va-summary-redeemed">
                {report.summary.counts_by_status?.REDEEMED || 0}
              </CardTitle>
              <CardDescription className="text-xs">
                {fmtR(report.summary.total_redeemed_value)}
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="border-amber-200 bg-amber-50/40">
            <CardHeader className="pb-2">
              <CardDescription>Expired</CardDescription>
              <CardTitle className="text-2xl text-amber-700" data-testid="va-summary-expired">
                {report.summary.counts_by_status?.EXPIRED || 0}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-purple-200">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <TrendingUp className="h-4 w-4" /> Total Issued Value
              </CardDescription>
              <CardTitle className="text-2xl" data-testid="va-summary-value">
                {fmtR(report.summary.total_issued_value)}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* By promotion breakdown */}
      {report?.summary?.by_promotion?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Breakdown by Promotion</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-gray-600 uppercase">
                <tr>
                  <th className="py-2">Promotion</th>
                  <th className="py-2 text-right">Issued</th>
                  <th className="py-2 text-right">Redeemed</th>
                  <th className="py-2 text-right">Outstanding</th>
                  <th className="py-2 text-right">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {report.summary.by_promotion.map((p) => (
                  <tr key={p.promotion_id} className="border-b last:border-0">
                    <td className="py-2">{p.name}</td>
                    <td className="py-2 text-right font-semibold">{p.count}</td>
                    <td className="py-2 text-right text-gray-700">
                      {p.redeemed_count || 0} · {fmtR(p.redeemed_value || 0)}
                    </td>
                    <td className="py-2 text-right text-emerald-700">
                      {p.outstanding_count || 0} · {fmtR(p.outstanding_value || 0)}
                    </td>
                    <td className="py-2 text-right">{fmtR(p.total_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Transactions */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            Voucher Transactions ({report?.transactions?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-gray-500">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              Loading…
            </div>
          ) : !report?.transactions?.length ? (
            <div className="py-8 text-center text-gray-500" data-testid="va-empty">
              No spend-reward vouchers were issued in the selected period.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="va-transactions-table">
                <thead className="border-b text-left text-xs text-gray-600 uppercase sticky top-0 bg-white">
                  <tr>
                    <th className="py-2 pr-3">Issued</th>
                    <th className="py-2 pr-3">Code</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Promotion</th>
                    <th className="py-2 pr-3">Sale #</th>
                    <th className="py-2 pr-3">Cashier</th>
                    <th className="py-2 pr-3">Customer</th>
                    <th className="py-2 pr-3">Expires</th>
                    <th className="py-2 pr-3">Redeemed</th>
                    <th className="py-2 text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {report.transactions.map((t) => (
                    <tr
                      key={t.code || `${t.sale_id}-${t.promotion_id}`}
                      className="border-b last:border-0 hover:bg-gray-50"
                    >
                      <td className="py-2 pr-3">{fmtDateTime(t.created_at)}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{t.code || "—"}</td>
                      <td className="py-2 pr-3">
                        <Badge
                          className={
                            t.status === "ACTIVE"
                              ? "bg-emerald-600"
                              : t.status === "REDEEMED"
                              ? "bg-gray-500"
                              : t.status === "EXPIRED"
                              ? "bg-amber-600"
                              : t.status === "CANCELLED"
                              ? "bg-red-600"
                              : "bg-slate-400"
                          }
                        >
                          {t.status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3">{t.promotion_name || "—"}</td>
                      <td className="py-2 pr-3 font-mono text-xs">
                        <div className="flex flex-col leading-tight">
                          {t.transaction_number ? (
                            <span title="Sale that issued this voucher">{t.transaction_number}</span>
                          ) : <span>—</span>}
                          {t.redeemed_sale_number ? (
                            <span className="text-[10px] text-purple-700" title="Sale where this voucher was redeemed">
                              redeemed: {t.redeemed_sale_number}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-2 pr-3">{t.cashier_name || "—"}</td>
                      <td className="py-2 pr-3">{t.customer_name || "—"}</td>
                      <td className="py-2 pr-3 text-xs">
                        {t.expiry_date
                          ? new Date(t.expiry_date).toLocaleDateString("en-ZA", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "—"}
                      </td>
                      <td className="py-2 pr-3 text-xs">
                        {t.redeemed_at ? (
                          <span>
                            {new Date(t.redeemed_at).toLocaleDateString("en-ZA", {
                              day: "2-digit",
                              month: "short",
                            })}
                            {t.redeemed_as ? ` (${t.redeemed_as})` : ""}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-2 text-right font-semibold text-purple-700">
                        {fmtR(t.voucher_value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
